import React, { forwardRef, type PropsWithChildren } from 'react';
import type { View } from 'react-native';
import { StyleSheet } from 'react-native';
import {
  Button as PaperButton,
  type ButtonProps as PaperButtonProps,
} from 'react-native-paper';
import { Colors } from '../constants/Colors';

export enum ButtonVariants {
  WHITE,
  PURPLE,
  DARK,
  DARK_50,
  CLEAR,
  PINK,
  ORANGE,
  RED,
  YELLOW,
}

export type ButtonProps = {
  size?: 'small' | 'medium' | 'large';
  variant?: ButtonVariants;
  loading?: boolean;
  hideChildrenOnLoading?: boolean;
} & PaperButtonProps;

function ButtonComponent(
  {
    size = 'medium',
    variant = ButtonVariants.WHITE,
    children,
    style,
    contentStyle,
    labelStyle,
    mode = 'contained',
    hideChildrenOnLoading,
    loading,
    ...otherProps
  }: PropsWithChildren<ButtonProps>,
  ref: React.Ref<View>,
) {
  const getVariantColors = () => {
    switch (variant) {
      case ButtonVariants.WHITE:
        return {
          backgroundColor: Colors.opacity20,
          borderColor: Colors.text,
        };
      case ButtonVariants.PURPLE:
        return {
          backgroundColor: Colors.purple1,
          borderColor: Colors.purple1,
        };
      case ButtonVariants.PINK:
        return {
          backgroundColor: Colors.pink0,
          borderColor: Colors.pink0,
        };
      case ButtonVariants.ORANGE:
        return {
          backgroundColor: Colors.orange0,
          borderColor: Colors.orange0,
        };
      case ButtonVariants.DARK:
        return {
          backgroundColor: Colors.purple0,
          borderColor: Colors.purple0,
        };
      case ButtonVariants.DARK_50:
        return {
          backgroundColor: Colors.purple0opacity50,
          borderColor: Colors.purple0opacity50,
        };
      case ButtonVariants.CLEAR:
        return {
          backgroundColor: 'transparent',
          borderColor: Colors.purple1,
        };
      case ButtonVariants.RED:
        return {
          backgroundColor: '#FF0000',
          borderColor: '#FF0000',
        };
      case ButtonVariants.YELLOW:
        return {
          backgroundColor: Colors.yellow0,
          borderColor: Colors.yellow0,
        };
      default:
        return {
          backgroundColor: Colors.opacity20,
          borderColor: Colors.text,
        };
    }
  };

  const { backgroundColor, borderColor } = getVariantColors();

  const getOutlinedStyle = () => {
    if (mode === 'outlined') {
      return {
        borderColor,
        borderWidth: 1,
      };
    }
    return {};
  };

  return (
    <PaperButton
      ref={ref}
      mode={mode}
      buttonColor={backgroundColor}
      textColor={Colors.text}
      style={[styles[size], getOutlinedStyle(), style]}
      contentStyle={[styles[`${size}Content`], contentStyle]}
      labelStyle={[
        styles[`${size}Label`],
        loading && hideChildrenOnLoading && styles.placeholderButtonLabel,
        labelStyle,
      ]}
      loading={loading}
      {...otherProps}
    >
      {hideChildrenOnLoading && loading ? '' : children}
    </PaperButton>
  );
}

export const Button = forwardRef(ButtonComponent);

const styles = StyleSheet.create({
  small: {
    borderRadius: 15,
  },
  medium: {
    borderRadius: 22,
  },
  large: {
    borderRadius: 28,
  },
  smallContent: {
    height: 30,
  },
  mediumContent: {
    height: 40,
  },
  largeContent: {
    height: 56,
  },
  smallLabel: {
    marginVertical: 5,
    fontSize: 12,
  },
  mediumLabel: {
    fontSize: 16,
  },
  largeLabel: {
    fontSize: 20,
  },
  placeholderButtonLabel: {
    marginLeft: 16,
    marginRight: 16,
  },
});
